import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Bar, Line } from "react-chartjs-2";

import { ChartPanel, DashboardsPageFrame, KpiCard, KpiGrid } from "./dashboardKit";
import { productPrioritiesQueryOptions } from "./dashboardsReactQuery";
import type {
  ProductPrioritiesDeferredMetric,
  ProductPrioritiesEfficiencyRow,
  ProductPrioritiesLoadDistributionRow,
  ProductPrioritiesNorthStarRow,
  ProductPrioritiesResponse,
} from "./productPrioritiesTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
);

const TARGET_COLOR = "#0F766E";
const TRAJECTORY_COLOR = "#2563EB";
const QUADRATIC_COLOR = "#B45309";
const EXPONENTIAL_COLOR = "#7C3AED";
const OPTIMISTIC_COLOR = "#D97706";
const GRID = "#ECE6DD";
const STONE = "#6D6A66";
const DEFAULT_TARGET_ALIGNED_CARESPACES = 7000;

const wholeNumber = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const oneDecimal = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

type TrajectoryId = "linear" | "quadratic" | "exponential";

const NORTH_STAR_TRAJECTORIES: Array<{
  borderColor: string;
  borderDash: number[];
  id: TrajectoryId;
  label: string;
  shortLabel: string;
  tension: number;
}> = [
  {
    id: "linear",
    label: "Linear fit",
    shortLabel: "Linear",
    borderColor: TRAJECTORY_COLOR,
    borderDash: [6, 5],
    tension: 0.2,
  },
  {
    id: "quadratic",
    label: "Quadratic fit",
    shortLabel: "Quadratic",
    borderColor: QUADRATIC_COLOR,
    borderDash: [10, 4, 2, 4],
    tension: 0.18,
  },
  {
    id: "exponential",
    label: "Exponential fit",
    shortLabel: "Exponential",
    borderColor: EXPONENTIAL_COLOR,
    borderDash: [2, 4],
    tension: 0.18,
  },
];

function fmtInt(value: number): string {
  return wholeNumber.format(Number(value || 0));
}

function fmtOneDecimal(value: number): string {
  return oneDecimal.format(Number(value || 0));
}

function fmtMonthLabel(monthStart: string): string {
  return new Date(`${monthStart}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
  });
}

function fmtAsOf(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
}

function roundOrNull(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value);
}

function fmtIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function isFullMonthSnapshot(row: {
  monthStart?: string | null;
  snapshotDate?: string | null;
}): boolean {
  if (!row?.monthStart || !row?.snapshotDate) return false;

  const monthStart = new Date(`${row.monthStart}T12:00:00Z`);
  const monthEnd = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 0));

  return row.snapshotDate.slice(0, 10) === fmtIsoDate(monthEnd);
}

function getCompletedMonthPoints<T extends { monthStart: string; snapshotDate: string | null }>(
  rows: T[],
  valueKey: keyof T,
): Array<{ index: number; isFullMonth: boolean; y: number }> {
  return rows
    .map((row, index) => ({
      index,
      isFullMonth: isFullMonthSnapshot(row),
      y: Number(row[valueKey]),
    }))
    .filter((point) => point.isFullMonth && Number.isFinite(point.y));
}

function calculateCurrentTrajectory<T extends { monthStart: string; snapshotDate: string | null }>(
  rows: T[],
  valueKey: keyof T,
): { projectedEoy: number; series: Array<number | null>; slopePerMonth: number } {
  const actualPoints = getCompletedMonthPoints(rows, valueKey);

  if (actualPoints.length === 0) {
    return {
      slopePerMonth: 0,
      series: rows.map(() => null),
      projectedEoy: 0,
    };
  }

  if (actualPoints.length < 2) {
    const firstPoint = actualPoints[0]!;
    return {
      slopePerMonth: 0,
      series: rows.map((_, index) => (index < firstPoint.index ? null : roundOrNull(firstPoint.y))),
      projectedEoy: firstPoint.y,
    };
  }

  const firstPoint = actualPoints[0]!;
  const latestPoint = actualPoints[actualPoints.length - 1]!;
  const elapsedMonths = latestPoint.index - firstPoint.index;
  const slopePerMonth = elapsedMonths > 0 ? (latestPoint.y - firstPoint.y) / elapsedMonths : 0;

  return {
    slopePerMonth,
    series: rows.map((_, index) =>
      roundOrNull(firstPoint.y + slopePerMonth * (index - firstPoint.index)),
    ),
    projectedEoy: latestPoint.y + slopePerMonth * (rows.length - 1 - latestPoint.index),
  };
}

function solveLinearSystem(matrix: number[][], values: number[]): number[] | null {
  const augmented = matrix.map((row, index) => [...row, values[index]!]);
  const size = augmented.length;

  for (let pivotCol = 0; pivotCol < size; pivotCol += 1) {
    let pivotRow = pivotCol;
    for (let row = pivotCol + 1; row < size; row += 1) {
      if (Math.abs(augmented[row]![pivotCol]!) > Math.abs(augmented[pivotRow]![pivotCol]!)) {
        pivotRow = row;
      }
    }

    if (Math.abs(augmented[pivotRow]![pivotCol]!) < 1e-9) return null;

    if (pivotRow !== pivotCol) {
      const temp = augmented[pivotCol]!;
      augmented[pivotCol] = augmented[pivotRow]!;
      augmented[pivotRow] = temp;
    }

    const pivotValue = augmented[pivotCol]![pivotCol]!;
    for (let col = pivotCol; col <= size; col += 1) {
      augmented[pivotCol]![col]! /= pivotValue;
    }

    for (let row = 0; row < size; row += 1) {
      if (row === pivotCol) continue;
      const factor = augmented[row]![pivotCol]!;
      for (let col = pivotCol; col <= size; col += 1) {
        augmented[row]![col]! -= factor * augmented[pivotCol]![col]!;
      }
    }
  }

  return augmented.map((row) => row[size]!);
}

function calculateQuadraticTrajectory<
  T extends { monthStart: string; snapshotDate: string | null },
>(rows: T[], valueKey: keyof T): { series: Array<number | null> } {
  const actualPoints = getCompletedMonthPoints(rows, valueKey);

  if (actualPoints.length < 3) {
    return { series: calculateCurrentTrajectory(rows, valueKey).series };
  }

  let sumX = 0;
  let sumX2 = 0;
  let sumX3 = 0;
  let sumX4 = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2Y = 0;

  actualPoints.forEach(({ index, y }) => {
    sumX += index;
    sumX2 += index ** 2;
    sumX3 += index ** 3;
    sumX4 += index ** 4;
    sumY += y;
    sumXY += index * y;
    sumX2Y += index ** 2 * y;
  });

  const coefficients = solveLinearSystem(
    [
      [sumX4, sumX3, sumX2],
      [sumX3, sumX2, sumX],
      [sumX2, sumX, actualPoints.length],
    ],
    [sumX2Y, sumXY, sumY],
  );

  if (!coefficients) {
    return { series: calculateCurrentTrajectory(rows, valueKey).series };
  }

  const [a, b, c] = coefficients;

  return {
    series: rows.map((_, index) => roundOrNull(a! * index ** 2 + b! * index + c!)),
  };
}

function calculateExponentialTrajectory<
  T extends { monthStart: string; snapshotDate: string | null },
>(rows: T[], valueKey: keyof T): { series: Array<number | null> } {
  const actualPoints = getCompletedMonthPoints(rows, valueKey).filter((point) => point.y > 0);

  if (actualPoints.length < 2) {
    return { series: calculateCurrentTrajectory(rows, valueKey).series };
  }

  const logPoints = actualPoints.map((point) => ({
    index: point.index,
    logY: Math.log(point.y),
  }));

  const pointCount = logPoints.length;
  const sumX = logPoints.reduce((sum, point) => sum + point.index, 0);
  const sumLogY = logPoints.reduce((sum, point) => sum + point.logY, 0);
  const sumX2 = logPoints.reduce((sum, point) => sum + point.index ** 2, 0);
  const sumXLogY = logPoints.reduce((sum, point) => sum + point.index * point.logY, 0);
  const denominator = pointCount * sumX2 - sumX ** 2;

  if (Math.abs(denominator) < 1e-9) {
    return { series: calculateCurrentTrajectory(rows, valueKey).series };
  }

  const slope = (pointCount * sumXLogY - sumX * sumLogY) / denominator;
  const intercept = (sumLogY - slope * sumX) / pointCount;

  return {
    series: rows.map((_, index) => roundOrNull(Math.exp(intercept + slope * index))),
  };
}

function calculateOptimisticQuadraticSeries<
  T extends { monthStart: string; snapshotDate: string | null },
>(rows: T[], valueKey: keyof T, targetValue: number): Array<number | null> {
  const actualPoints = getCompletedMonthPoints(rows, valueKey);
  const targetIndex = rows.length - 1;

  if (targetIndex < 0) return [];
  if (actualPoints.length < 2) {
    const latestActualPoint = [...rows]
      .map((row, index) => ({
        index,
        y: Number(row[valueKey]),
        hasSnapshot: Boolean(row.snapshotDate),
      }))
      .reverse()
      .find((point) => point.hasSnapshot && Number.isFinite(point.y));

    if (!latestActualPoint) return rows.map(() => null);

    const remainingSteps = targetIndex - latestActualPoint.index;
    const perMonthIncrease =
      remainingSteps > 0 ? (targetValue - latestActualPoint.y) / remainingSteps : 0;

    return rows.map((_, index) => {
      if (index < latestActualPoint.index) return null;
      return roundOrNull(
        latestActualPoint.y + perMonthIncrease * (index - latestActualPoint.index),
      );
    });
  }

  let sumUU = 0;
  let sumUV = 0;
  let sumVV = 0;
  let sumUZ = 0;
  let sumVZ = 0;

  actualPoints.forEach(({ index, y }) => {
    const u = index ** 2 - targetIndex ** 2;
    const v = index - targetIndex;
    const z = y - targetValue;

    sumUU += u * u;
    sumUV += u * v;
    sumVV += v * v;
    sumUZ += u * z;
    sumVZ += v * z;
  });

  const determinant = sumUU * sumVV - sumUV ** 2;
  if (Math.abs(determinant) < 1e-9) {
    return calculateCurrentTrajectory(rows, valueKey).series;
  }

  const a = (sumUZ * sumVV - sumVZ * sumUV) / determinant;
  const b = (sumUU * sumVZ - sumUV * sumUZ) / determinant;
  const c = targetValue - a * targetIndex ** 2 - b * targetIndex;

  return rows.map((_, index) => roundOrNull(a * index ** 2 + b * index + c));
}

function buildNorthStarSummary(
  data: ProductPrioritiesResponse,
  targetAlignedCarespaces: number,
): {
  currentValue: number;
  exponentialTrajectorySeries: Array<number | null>;
  gapToTarget: number;
  optimisticTrajectorySeries: Array<number | null>;
  projectedEoy: number;
  quadraticTrajectorySeries: Array<number | null>;
  requiredAddsPerMonth: number;
  trajectorySeries: Array<number | null>;
} {
  const currentRow = [...data.northStarRows]
    .reverse()
    .find((row) => row.snapshotDate && row.alignedCarespaces !== null);
  const trajectory = calculateCurrentTrajectory(data.northStarRows, "alignedCarespaces");
  const quadraticTrajectory = calculateQuadraticTrajectory(data.northStarRows, "alignedCarespaces");
  const exponentialTrajectory = calculateExponentialTrajectory(
    data.northStarRows,
    "alignedCarespaces",
  );
  const currentValue = Number(currentRow?.alignedCarespaces || 0);
  const currentIndex = data.northStarRows.findLastIndex((row) => row.snapshotDate);
  const remainingSteps = data.northStarRows.length - 1 - currentIndex;

  return {
    currentValue,
    projectedEoy: trajectory.projectedEoy,
    gapToTarget: Math.max(0, targetAlignedCarespaces - currentValue),
    requiredAddsPerMonth:
      remainingSteps > 0
        ? Math.max(0, (targetAlignedCarespaces - currentValue) / remainingSteps)
        : 0,
    trajectorySeries: trajectory.series,
    quadraticTrajectorySeries: quadraticTrajectory.series,
    exponentialTrajectorySeries: exponentialTrajectory.series,
    optimisticTrajectorySeries: calculateOptimisticQuadraticSeries(
      data.northStarRows,
      "alignedCarespaces",
      targetAlignedCarespaces,
    ),
  };
}

function buildEfficiencySummary(
  rows: ProductPrioritiesEfficiencyRow[],
  targetAlignedCarespaces: number,
): {
  cnGap: number;
  currentAlignedPerNavigator: number;
  currentAssignedAlignedCarespaces: number;
  currentNavigators: number;
  requiredNavigatorsAtTarget: number;
} {
  const currentRow = [...rows]
    .reverse()
    .find((row) => row.snapshotDate && row.alignedPerNavigator !== null);

  const currentAlignedPerNavigator = Number(currentRow?.alignedPerNavigator || 0);
  const currentNavigators = Number(currentRow?.navigatorsWithAlignedCarespaces || 0);
  const requiredNavigatorsAtTarget =
    currentAlignedPerNavigator > 0
      ? Math.ceil(targetAlignedCarespaces / currentAlignedPerNavigator)
      : 0;

  return {
    currentAlignedPerNavigator,
    currentAssignedAlignedCarespaces: Number(currentRow?.assignedAlignedCarespaces || 0),
    currentNavigators,
    requiredNavigatorsAtTarget,
    cnGap: Math.max(0, requiredNavigatorsAtTarget - currentNavigators),
  };
}

function calculateMean(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function calculateStdDev(values: number[], mean: number): number {
  if (values.length < 2) return 0;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function normalPdf(x: number, mean: number, stdDev: number): number {
  if (stdDev <= 0) return 0;
  const exponent = -((x - mean) ** 2) / (2 * stdDev ** 2);
  return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
}

function buildLoadDistribution(rows: ProductPrioritiesLoadDistributionRow[]): {
  bellCurve: number[];
  frequencies: number[];
  labels: string[];
  mean: number;
  stdDev: number;
} {
  const loads = rows
    .map((row) => Number(row.alignedCarespaces || 0))
    .filter((value) => Number.isFinite(value));

  if (!loads.length) {
    return {
      labels: [],
      frequencies: [],
      bellCurve: [],
      mean: 0,
      stdDev: 0,
    };
  }

  const minLoad = Math.min(...loads);
  const maxLoad = Math.max(...loads);
  const distinctLoads = new Set(loads).size;
  const suggestedBinCount = Math.max(4, Math.min(8, Math.ceil(Math.log2(loads.length) + 1)));
  const binCount = Math.min(Math.max(1, distinctLoads), suggestedBinCount);
  const range = Math.max(1, maxLoad - minLoad + 1);
  const binWidth = Math.max(1, Math.ceil(range / binCount));

  const bins = Array.from({ length: binCount }, (_, index) => {
    const start = minLoad + index * binWidth;
    const end = Math.min(maxLoad, start + binWidth - 1);
    return {
      start,
      end,
      frequency: 0,
      center: start + (end - start) / 2,
    };
  }).filter((bin) => bin.start <= maxLoad);

  loads.forEach((value) => {
    const binIndex = Math.min(bins.length - 1, Math.floor((value - minLoad) / binWidth));
    bins[binIndex]!.frequency += 1;
  });

  const mean = calculateMean(loads);
  const stdDev = calculateStdDev(loads, mean);
  const bellCurve =
    stdDev === 0
      ? bins.map((bin) => (mean >= bin.start && mean <= bin.end ? loads.length : 0))
      : bins.map((bin) => loads.length * binWidth * normalPdf(bin.center, mean, stdDev));

  return {
    labels: bins.map((bin) =>
      bin.start === bin.end ? String(bin.start) : `${bin.start}-${bin.end}`,
    ),
    frequencies: bins.map((bin) => bin.frequency),
    bellCurve,
    mean,
    stdDev,
  };
}

function SectionHeader(props: {
  action?: ReactNode;
  eyebrow: string;
  subtitle: string;
  title: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <p className="text-[0.65rem] font-medium tracking-wide text-muted-foreground uppercase">
            {props.eyebrow}
          </p>
          <h2 className="mt-1 mb-1.5 text-lg font-semibold tracking-tight text-foreground">
            {props.title}
          </h2>
          <p className="text-xs leading-relaxed text-muted-foreground">{props.subtitle}</p>
        </div>
        {props.action}
      </div>
    </div>
  );
}

function DeferredMetricCard(props: { label: string; reason: string }) {
  return (
    <article className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-4 py-3">
      <p className="text-xs text-muted-foreground">{props.label}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{props.reason}</p>
    </article>
  );
}

function ProductPrioritiesContent(props: { data: ProductPrioritiesResponse }) {
  const { data } = props;
  const [selectedTrajectoryIds, setSelectedTrajectoryIds] = useState<TrajectoryId[]>(["quadratic"]);
  const [targetAlignedCarespaces, setTargetAlignedCarespaces] = useState(() =>
    Number.isFinite(data.targetAlignedCarespaces)
      ? data.targetAlignedCarespaces
      : DEFAULT_TARGET_ALIGNED_CARESPACES,
  );
  const northStarSummary = useMemo(
    () => buildNorthStarSummary(data, targetAlignedCarespaces),
    [data, targetAlignedCarespaces],
  );
  const efficiencySummary = useMemo(
    () => buildEfficiencySummary(data.careNavigationEfficiencyRows, targetAlignedCarespaces),
    [data.careNavigationEfficiencyRows, targetAlignedCarespaces],
  );
  const loadDistribution = useMemo(
    () => buildLoadDistribution(data.currentCarryingLoadRows || []),
    [data.currentCarryingLoadRows],
  );
  const selectedTrajectoryLabels = NORTH_STAR_TRAJECTORIES.filter((trajectory) =>
    selectedTrajectoryIds.includes(trajectory.id),
  ).map((trajectory) => trajectory.shortLabel);
  const northStarTrajectoryDatasets = useMemo(
    () =>
      NORTH_STAR_TRAJECTORIES.filter((trajectory) =>
        selectedTrajectoryIds.includes(trajectory.id),
      ).map((trajectory) => {
        let dataSeries = northStarSummary.trajectorySeries;
        if (trajectory.id === "quadratic") {
          dataSeries = northStarSummary.quadraticTrajectorySeries;
        }
        if (trajectory.id === "exponential") {
          dataSeries = northStarSummary.exponentialTrajectorySeries;
        }

        return {
          label: trajectory.label,
          data: dataSeries,
          borderColor: trajectory.borderColor,
          backgroundColor: "transparent",
          borderDash: trajectory.borderDash,
          tension: trajectory.tension,
          pointRadius: 0,
          spanGaps: true,
        };
      }),
    [
      northStarSummary.exponentialTrajectorySeries,
      northStarSummary.quadraticTrajectorySeries,
      northStarSummary.trajectorySeries,
      selectedTrajectoryIds,
    ],
  );

  const northStarLabels = data.northStarRows.map((row: ProductPrioritiesNorthStarRow) =>
    fmtMonthLabel(row.monthStart),
  );
  const northStarChartData = useMemo(
    () => ({
      labels: northStarLabels,
      datasets: [
        {
          label: "Actual aligned carespaces",
          data: data.northStarRows.map((row) => row.alignedCarespaces),
          borderColor: TARGET_COLOR,
          backgroundColor: "rgba(15, 118, 110, 0.12)",
          pointBackgroundColor: TARGET_COLOR,
          fill: true,
          tension: 0.28,
          pointRadius: 3,
          spanGaps: false,
        },
        ...northStarTrajectoryDatasets,
        {
          label: `Optimistic path to ${fmtInt(targetAlignedCarespaces)}`,
          data: northStarSummary.optimisticTrajectorySeries,
          borderColor: OPTIMISTIC_COLOR,
          backgroundColor: "transparent",
          borderDash: [3, 4],
          tension: 0.22,
          pointRadius: 0,
          spanGaps: true,
        },
      ],
    }),
    [
      data.northStarRows,
      northStarLabels,
      northStarSummary.optimisticTrajectorySeries,
      northStarTrajectoryDatasets,
      targetAlignedCarespaces,
    ],
  );

  const northStarChartOptions = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom" },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: STONE },
        },
        y: {
          grid: { color: GRID },
          ticks: {
            color: STONE,
            callback: (value) => wholeNumber.format(Number(value || 0)),
          },
        },
      },
    }),
    [],
  );

  const efficiencyChartData = useMemo(
    () => ({
      labels: data.careNavigationEfficiencyRows.map((row) => fmtMonthLabel(row.monthStart)),
      datasets: [
        {
          label: "Aligned carespaces per care navigator",
          data: data.careNavigationEfficiencyRows.map((row) => row.alignedPerNavigator),
          borderColor: TRAJECTORY_COLOR,
          backgroundColor: "rgba(37, 99, 235, 0.12)",
          pointBackgroundColor: TRAJECTORY_COLOR,
          fill: true,
          tension: 0.24,
          pointRadius: 3,
          spanGaps: false,
        },
      ],
    }),
    [data.careNavigationEfficiencyRows],
  );

  const efficiencyChartOptions = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: STONE },
        },
        y: {
          grid: { color: GRID },
          ticks: {
            color: STONE,
            callback: (value) => fmtOneDecimal(Number(value)),
          },
        },
      },
    }),
    [],
  );

  const loadDistributionChartData = useMemo(
    () =>
      ({
        labels: loadDistribution.labels,
        datasets: [
          {
            type: "bar" as const,
            label: "Care navigators",
            data: loadDistribution.frequencies,
            backgroundColor: "rgba(15, 118, 110, 0.72)",
            borderColor: TARGET_COLOR,
            borderWidth: 1,
            borderRadius: 4,
          },
          {
            type: "line" as const,
            label: "Normal curve",
            data: loadDistribution.bellCurve,
            borderColor: OPTIMISTIC_COLOR,
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.28,
            yAxisID: "y",
          },
        ],
      }) as ChartData<"bar">,
    [loadDistribution],
  );

  const loadDistributionChartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            afterBody(items: TooltipItem<"bar">[]) {
              if (!items?.length) return [];
              return [
                `Mean load: ${fmtOneDecimal(loadDistribution.mean)}`,
                `Std dev: ${fmtOneDecimal(loadDistribution.stdDev)}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: STONE },
          title: {
            display: true,
            text: "Aligned carespaces per care navigator",
            color: STONE,
          },
        },
        y: {
          grid: { color: GRID },
          ticks: {
            color: STONE,
            precision: 0,
          },
          title: {
            display: true,
            text: "Care navigators",
            color: STONE,
          },
        },
      },
    }),
    [loadDistribution.mean, loadDistribution.stdDev],
  );

  return (
    <div className="flex flex-col gap-7">
      <section className="grid gap-4">
        <SectionHeader
          eyebrow="North Star"
          title={`${fmtInt(targetAlignedCarespaces)} aligned carespaces by end of year`}
          subtitle={`Monthly aligned-carespace actuals with selectable linear, quadratic, and exponential fits, plus an always-on optimistic path to ${fmtInt(targetAlignedCarespaces)}. All fitted trajectories use completed months only. Excludes software-only organizations.`}
          action={
            <label
              htmlFor="product-priorities-target"
              className="grid shrink-0 gap-1.5 text-xs font-semibold text-muted-foreground"
            >
              EOY target
              <input
                id="product-priorities-target"
                aria-label="EOY target"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={targetAlignedCarespaces}
                onChange={(event) => {
                  const targetValue = event.currentTarget.valueAsNumber;
                  setTargetAlignedCarespaces(
                    Number.isFinite(targetValue) ? Math.max(0, Math.trunc(targetValue)) : 0,
                  );
                }}
                className="w-32 rounded-md border border-border/70 bg-background px-2.5 py-2 text-sm text-foreground"
              />
            </label>
          }
        />
        <KpiGrid>
          <KpiCard
            label="Current aligned carespaces"
            value={fmtInt(northStarSummary.currentValue)}
          />
          <KpiCard label="EOY target" value={fmtInt(targetAlignedCarespaces)} />
          <KpiCard
            label="Projected Dec 2026"
            value={fmtInt(northStarSummary.projectedEoy)}
            footnote="Linear projection"
          />
          <KpiCard label="Gap to target" value={fmtInt(northStarSummary.gapToTarget)} />
          <KpiCard
            label="Required monthly net adds"
            value={fmtInt(northStarSummary.requiredAddsPerMonth)}
            footnote="Aug–Dec pace"
          />
        </KpiGrid>
        <ChartPanel
          title="Aligned Carespaces Trend"
          action={
            <details className="relative">
              <summary className="cursor-pointer list-none text-xs text-muted-foreground select-none">
                Trajectories: {selectedTrajectoryLabels.join(", ") || "None"}
              </summary>
              <div className="absolute top-[calc(100%+0.5rem)] right-0 z-2 min-w-44 rounded-lg border border-border/70 bg-background px-3.5 py-3 shadow-md">
                {NORTH_STAR_TRAJECTORIES.map((trajectory) => (
                  <label
                    key={trajectory.id}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTrajectoryIds.includes(trajectory.id)}
                      onChange={() =>
                        setSelectedTrajectoryIds((currentIds) =>
                          currentIds.includes(trajectory.id)
                            ? currentIds.filter((id) => id !== trajectory.id)
                            : [...currentIds, trajectory.id],
                        )
                      }
                    />
                    {trajectory.shortLabel}
                  </label>
                ))}
              </div>
            </details>
          }
        >
          <div className="relative h-80">
            <Line data={northStarChartData} options={northStarChartOptions} />
          </div>
        </ChartPanel>
      </section>

      <section className="grid gap-4">
        <SectionHeader
          eyebrow="Care Navigation"
          title="Efficiency"
          subtitle="Current carrying load across care navigators actively carrying aligned carespaces. This ratio counts unique users with the `Care Navigator` role who currently have aligned carespaces assigned, and excludes software-only organizations."
        />
        <KpiGrid>
          <KpiCard
            label="Care navigators with aligned carespaces"
            value={fmtInt(efficiencySummary.currentNavigators)}
            footnote="Unique Care Navigator users"
          />
          <KpiCard
            label="Aligned carespaces per care navigator"
            value={fmtOneDecimal(efficiencySummary.currentAlignedPerNavigator)}
            footnote="Current carrying load"
          />
          <KpiCard
            label="Assigned aligned carespaces"
            value={fmtInt(efficiencySummary.currentAssignedAlignedCarespaces)}
          />
          <KpiCard
            label="CN gap"
            value={fmtInt(efficiencySummary.cnGap)}
            footnote={`${fmtInt(efficiencySummary.requiredNavigatorsAtTarget)} total CNs needed at current load`}
          />
        </KpiGrid>
        <div className="grid items-stretch gap-4 [grid-template-columns:repeat(auto-fit,minmax(360px,1fr))]">
          <ChartPanel title="Staffing Load Trend">
            <div className="relative h-64">
              <Line data={efficiencyChartData} options={efficiencyChartOptions} />
            </div>
          </ChartPanel>
          <ChartPanel
            title="Current Load Distribution"
            action={<span className="text-xs text-muted-foreground">Histogram + normal curve</span>}
          >
            <div className="relative h-64">
              <Bar data={loadDistributionChartData} options={loadDistributionChartOptions} />
            </div>
          </ChartPanel>
        </div>
        <ChartPanel
          title="Deferred Metrics"
          action={<span className="text-xs text-muted-foreground">Blocked by schema linkage</span>}
        >
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            {data.deferredMetrics.map((metric: ProductPrioritiesDeferredMetric) => (
              <DeferredMetricCard key={metric.key} label={metric.label} reason={metric.reason} />
            ))}
          </div>
        </ChartPanel>
      </section>
    </div>
  );
}

export function ProductPrioritiesDashboard() {
  const query = useQuery(productPrioritiesQueryOptions());

  return (
    <DashboardsPageFrame className="flex flex-col gap-3">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground">Product Priorities</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          North-star product metrics for 2026.
          {query.data ? ` Current month points are as of ${fmtAsOf(query.data.asOf)}.` : null}
        </p>
      </div>

      {query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading Product Priorities…
        </div>
      ) : null}

      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "Failed to load Product Priorities"}
        </div>
      ) : null}

      {query.data ? <ProductPrioritiesContent data={query.data} /> : null}
    </DashboardsPageFrame>
  );
}
